// Typed API client — talks to apps/api over fetch.
//
// Why not Eden Treaty yet?
//   Pass F-auth ships a hand-typed wrapper so the request/response shapes are
//   explicit at the call site. Eden Treaty integration is a follow-up — the
//   typegen barrel (packages/shared) exposes an opaque `App` placeholder; once
//   apps/web adds `@stumble/api` as a workspace dep we can swap in
//   `treaty<App>(BASE_URL)` here without changing screens.
//
// Dev:  Vite dev server proxies /api → http://localhost:3000 (apps/api)
// Prod: SPA + API share the same Bun container, /api is same-origin.

const BASE_URL = ''

export type ApiError = {
  status: number
  body: unknown
  message: string
}

export class StumbleApiError extends Error {
  status: number
  body: unknown
  constructor(error: ApiError) {
    super(error.message)
    this.status = error.status
    this.body = error.body
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  })
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    throw new StumbleApiError({
      status: res.status,
      body,
      message: `API ${path} → ${res.status}`,
    })
  }
  return body as T
}

/** Back-compat thin wrapper for legacy callers. */
export const apiFetch = request

// ───── Auth endpoints ────────────────────────────────────────

export type OtpRequestResponse = {
  expiresAt: string
  delivery: {
    provider: 'sendgrid' | 'mailpit' | 'log' | 'skip-test'
    sent: boolean
  }
  devCode: string | null
}

export type OtpVerifyOk = { ok: true; csrfToken: string }
export type OtpVerifyFail = {
  ok: false
  reason: 'invalid' | 'expired' | 'rate_limited'
}

export type CurrentUser = {
  id: string
  displayName: string
  email: string | null
  username: string | null
  friendCode: string | null
}

export const authApi = {
  requestOtp(email: string): Promise<OtpRequestResponse> {
    return request<OtpRequestResponse>('/api/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  async verifyOtp(email: string, code: string): Promise<OtpVerifyOk | OtpVerifyFail> {
    try {
      return await request<OtpVerifyOk>('/api/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ email, code }),
      })
    } catch (err) {
      if (err instanceof StumbleApiError && err.body && typeof err.body === 'object') {
        const b = err.body as {
          ok?: false
          reason?: 'invalid' | 'expired' | 'rate_limited'
        }
        if (b.ok === false && b.reason) return { ok: false, reason: b.reason }
      }
      throw err
    }
  },

  logout(): Promise<{ ok: true }> {
    return request<{ ok: true }>('/api/auth/logout', { method: 'POST' })
  },

  async me(): Promise<CurrentUser | null> {
    try {
      return await request<CurrentUser>('/api/me')
    } catch (err) {
      if (err instanceof StumbleApiError && err.status === 401) return null
      throw err
    }
  },
}

// ───── Domain types (mirror apps/api/src/routes/_serializers.ts) ────

export type VenueDto = {
  id: string
  name: string
  city: string | null
  address: string | null
  latitude: number | null
  longitude: number | null
  provider: string
  providerVenueId: string | null
  /**
   * Proxy URL to GET /api/venues/:id/photo (302 redirects to the R2-cached
   * Google Places photo). NULL when the venue has no upstream photo or for
   * un-materialized Google search results.
   *
   * Safe to drop directly into <img src={venue.photoUrl} /> — the API key
   * never leaks to the browser.
   */
  photoUrl: string | null
}

export type SessionVenueDto = {
  id: string
  nightSessionId: string
  venueId: string
  venue: VenueDto
  arrivedAt: string
  leftAt: string | null
  sequenceIndex: number
}

export type DrinkDto = {
  id: string
  nightSessionId: string
  sessionVenueId: string
  userId: string
  venueId: string
  drinkType: string
  drinkName: string | null
  createdAt: string
}

export type MediaDto = {
  id: string
  nightSessionId: string
  userId: string
  venueId: string | null
  mimeType: string
  /**
   * Inline base64 payload for `storage_provider='data_url'` rows. Populated in
   * dev / test (default storage path); null in prod where every selfie is
   * stored to R2 and surfaced via `publicUrl`.
   */
  dataUrl: string | null
  byteSize: number | null
  sha256: string | null
  storageProvider: string
  storageKey: string | null
  publicUrl: string | null
  filterPreset: string | null
  filterCss: string | null
  recapCaption: string | null
  createdAt: string
  expiresAt: string
}

export type NightSessionDto = {
  id: string
  userId: string
  title: string | null
  status: string
  visibility: string
  startedAt: string
  endedAt: string | null
}

export type NightDetailsDto = {
  night: NightSessionDto
  currentVenue: SessionVenueDto | null
  sessionVenues: SessionVenueDto[]
  drinks: DrinkDto[]
  media: MediaDto[]
  totals: {
    drinks: number
    venues: number
    byType: Record<string, number>
    byVenue: { venueId: string; venueName: string; drinks: number }[]
  }
}

// ───── Night API ──────────────────────────────────────────────

export const nightApi = {
  start(title?: string, visibility?: string): Promise<{ night: NightDetailsDto }> {
    return request<{ night: NightDetailsDto }>('/api/nights', {
      method: 'POST',
      body: JSON.stringify({ title, visibility }),
    })
  },

  get(id: string, init?: RequestInit): Promise<{ night: NightDetailsDto }> {
    return request<{ night: NightDetailsDto }>(`/api/nights/${id}`, init)
  },

  end(id: string): Promise<{ night: NightDetailsDto }> {
    return request<{ night: NightDetailsDto }>(`/api/nights/${id}/end`, {
      method: 'POST',
    })
  },

  /**
   * Check in to an existing venue (must already exist in /api/venues).
   * For NEW venues, call venuesApi.create() first, then pass the returned id.
   */
  addVenue(nightId: string, venueId: string): Promise<{ night: NightDetailsDto }> {
    return request<{ night: NightDetailsDto }>(`/api/nights/${nightId}/venues`, {
      method: 'POST',
      body: JSON.stringify({ venueId }),
    })
  },

  logDrink(
    nightId: string,
    drink: { drinkType: string; drinkName?: string | null },
  ): Promise<{ night: NightDetailsDto }> {
    return request<{ night: NightDetailsDto }>(`/api/nights/${nightId}/drinks`, {
      method: 'POST',
      body: JSON.stringify(drink),
    })
  },

  /**
   * Upload a selfie/proof blob to a night. The blob is base64-encoded into a
   * data URL client-side (server enforces ≤ 1.65 MB ceiling per POST validator).
   * Mime type defaults to `blob.type` (e.g. 'image/webp' from Stumble Cam).
   *
   * NOTE: dataUrl is the only working storage path today — R2 upload arrives in
   * a follow-up. Keeping the surface narrow so the swap is internal-only.
   */
  async uploadMedia(
    nightId: string,
    blob: Blob,
    opts?: { recapCaption?: string; filterPreset?: string; signal?: AbortSignal },
  ): Promise<{ media: MediaDto }> {
    const dataUrl = await blobToDataUrl(blob)
    return request<{ media: MediaDto }>(`/api/nights/${nightId}/media`, {
      method: 'POST',
      body: JSON.stringify({
        mimeType: blob.type || 'image/webp',
        dataUrl,
        byteSize: blob.size,
        recapCaption: opts?.recapCaption ?? null,
        filterPreset: opts?.filterPreset ?? null,
      }),
      signal: opts?.signal,
    })
  },
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.onload = () => {
      const r = reader.result
      if (typeof r === 'string') resolve(r)
      else reject(new Error('FileReader did not return a string'))
    }
    reader.readAsDataURL(blob)
  })
}

// ───── Venues API ─────────────────────────────────────────────

export const venuesApi = {
  async search(query: string, lat?: number, lng?: number): Promise<VenueDto[]> {
    const params = new URLSearchParams()
    if (query) params.set('q', query)
    if (lat != null) params.set('latitude', String(lat))
    if (lng != null) params.set('longitude', String(lng))
    try {
      const res = await request<{ venues: VenueDto[] }>(`/api/venues/search?${params.toString()}`)
      return res.venues
    } catch (err) {
      if (err instanceof StumbleApiError) return []
      throw err
    }
  },

  async create(input: {
    name: string
    city?: string | null
    address?: string | null
    latitude?: number | null
    longitude?: number | null
    provider?: 'local' | 'google_places'
    providerVenueId?: string | null
  }): Promise<VenueDto> {
    const res = await request<{ venue: VenueDto }>('/api/venues', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    return res.venue
  },
}

// ───── Active-night convenience ───────────────────────────────

export type UpdateProfileError =
  | 'invalid_display_name'
  | 'invalid_username'
  | 'username_reserved'
  | 'username_taken'

export type UpdateProfileResult =
  | { ok: true; user: CurrentUser }
  | { ok: false; reason: UpdateProfileError }

export const meApi = {
  /**
   * PATCH /api/me — update display name and/or username.
   * Returns a discriminated union so callers can render inline errors
   * without try/catching on HTTP status.
   */
  async update(input: { displayName?: string; username?: string }): Promise<UpdateProfileResult> {
    try {
      const user = await request<CurrentUser>('/api/me', {
        method: 'PATCH',
        body: JSON.stringify(input),
      })
      return { ok: true, user }
    } catch (err) {
      if (err instanceof StumbleApiError && err.body && typeof err.body === 'object') {
        const reason = (err.body as { error?: string }).error
        if (
          reason === 'invalid_display_name' ||
          reason === 'invalid_username' ||
          reason === 'username_reserved' ||
          reason === 'username_taken'
        ) {
          return { ok: false, reason }
        }
      }
      throw err
    }
  },

  async activeNight(): Promise<NightDetailsDto | null> {
    try {
      const res = await request<{ night: NightDetailsDto | null }>('/api/me/active-night')
      return res.night
    } catch (err) {
      if (err instanceof StumbleApiError && err.status === 401) return null
      return null
    }
  },

  async history(): Promise<NightDetailsDto[]> {
    try {
      const res = await request<{ nights: NightDetailsDto[] }>('/api/me/nights')
      return res.nights
    } catch {
      return []
    }
  },
}

// ───── Friends ────────────────────────────────────────────────

export type PublicUserDto = {
  id: string
  displayName: string
  username: string | null
  friendCode: string | null
  createdAt: string | null
}

export type FriendshipStatus = 'pending' | 'accepted' | 'declined'

export type FriendRequestDto = {
  id: string
  requesterId: string
  addresseeId: string
  status: FriendshipStatus
  createdAt: string
  respondedAt: string | null
  requester: PublicUserDto
  addressee: PublicUserDto
}

export type FriendsRequestsResponse = {
  incoming: FriendRequestDto[]
  outgoing: FriendRequestDto[]
}

export type UserSearchHit = {
  user: PublicUserDto
  relationship: 'self' | 'none' | 'incoming_pending' | 'outgoing_pending' | 'friends'
}

export const friendsApi = {
  async requests(): Promise<FriendsRequestsResponse> {
    try {
      return await request<FriendsRequestsResponse>('/api/friends/requests')
    } catch (err) {
      if (err instanceof StumbleApiError) return { incoming: [], outgoing: [] }
      throw err
    }
  },

  async search(query: string): Promise<UserSearchHit[]> {
    if (!query.trim()) return []
    const qs = new URLSearchParams({ query }).toString()
    try {
      const res = await request<{ users: UserSearchHit[] }>(`/api/friends/search?${qs}`)
      return res.users
    } catch {
      return []
    }
  },

  async accept(requestId: string): Promise<FriendRequestDto | null> {
    try {
      const res = await request<{ request: FriendRequestDto }>(
        `/api/friends/requests/${requestId}/accept`,
        { method: 'POST' },
      )
      return res.request
    } catch {
      return null
    }
  },
}

// ───── Stumbling (live board) ─────────────────────────────────

export type ActiveStumbleDto = {
  nightId: string
  ownerId: string
  ownerDisplayName: string
  ownerUsername: string | null
  visibility: string
  startedAt: string
  currentVenue: {
    id: string
    name: string
    city: string | null
  } | null
  drinkCount: number
  /** F-wave PR γ (F10): 'active' or 'ended' (within the 1hr warm window). */
  status: 'active' | 'ended'
  /** ISO timestamp when the night ended; null while active. */
  endedAt: string | null
}

export type ProofItemDto = {
  mediaId: string
  nightId: string
  ownerId: string
  ownerDisplayName: string
  /** R2 public URL (or storage-key proxy) — null in dev when only `dataUrl` exists. */
  photoUrl: string | null
  /** Inline base64 data URL — populated for `data_url` storage rows (dev/test). */
  dataUrl: string | null
  caption: string | null
  venueName: string | null
  capturedAt: string
  /** F-wave PR γ (F10): mirrors ActiveStumbleDto — UI ghost-state trigger. */
  status: 'active' | 'ended'
  endedAt: string | null
}

export const stumblingApi = {
  async live(): Promise<ActiveStumbleDto[]> {
    try {
      const res = await request<{ stumbles: ActiveStumbleDto[] }>('/api/stumbling')
      return res.stumbles
    } catch (err) {
      if (err instanceof StumbleApiError && err.status === 401) return []
      return []
    }
  },

  async proof(): Promise<ProofItemDto[]> {
    try {
      const res = await request<{ proofs: ProofItemDto[] }>('/api/stumbling/proof')
      return res.proofs
    } catch (err) {
      if (err instanceof StumbleApiError && err.status === 401) return []
      return []
    }
  },
}

/**
 * Dispatched on `window` immediately after a selfie POST succeeds. Live-board
 * listeners refetch /api/stumbling/proof so the new polaroid shows up without
 * waiting for the next 10s poll. Keep the event payload minimal — consumers
 * re-read the canonical feed rather than trusting client-shipped media rows.
 */
export const SELFIE_SAVED_EVENT = 'stumble:selfie-saved'

// ───── Squads / H2H battles ───────────────────────────────────

export type SquadColor = 'pink' | 'lime' | 'teal' | 'orange'
export type BattleRouteType = 'crawl' | 'freestyle'

export type SquadPublicMember = {
  id: string
  displayName: string
  username: string | null
}

export type SquadDto = {
  id: string
  name: string
  color: string
  createdBy: string
  createdAt: string
  endedAt: string | null
  members: SquadPublicMember[]
}

export type BattleVenueDto = {
  venueId: string
  venueName: string
  orderIdx: number
  claimedBy: string | null
  claimedAt: string | null
  countA: number
  countB: number
}

export type BattleDto = {
  id: string
  routeType: string
  startedAt: string
  endedAt: string | null
  squadA: SquadDto
  squadB: SquadDto
  venues: BattleVenueDto[]
  totals: {
    drinksA: number
    drinksB: number
    claimsA: number
    claimsB: number
  }
}

export const squadsApi = {
  create(input: { name: string; color: SquadColor }): Promise<{ squad: SquadDto }> {
    return request<{ squad: SquadDto }>('/api/squads', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  addMember(squadId: string, userId: string): Promise<{ squad: SquadDto }> {
    return request<{ squad: SquadDto }>(`/api/squads/${squadId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    })
  },

  startBattle(
    squadId: string,
    input: {
      opponentSquadId: string
      routeType: BattleRouteType
      venues?: Array<{ venueId: string; orderIdx: number }>
    },
  ): Promise<{ battle: BattleDto }> {
    return request<{ battle: BattleDto }>(`/api/squads/${squadId}/battles`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
}

export const battlesApi = {
  get(battleId: string): Promise<{ battle: BattleDto }> {
    return request<{ battle: BattleDto }>(`/api/battles/${battleId}`)
  },

  async claim(
    battleId: string,
    venueId: string,
  ): Promise<
    | { ok: true; battle: BattleDto }
    | { ok: false; reason: 'insufficient'; yourCount: number; theirCount: number }
    | { ok: false; reason: 'not_in_battle' | 'venue_not_in_battle' | 'not_found' }
  > {
    try {
      const res = await request<{ battle: BattleDto }>(`/api/battles/${battleId}/claim`, {
        method: 'POST',
        body: JSON.stringify({ venueId }),
      })
      return { ok: true, battle: res.battle }
    } catch (err) {
      if (err instanceof StumbleApiError && err.body && typeof err.body === 'object') {
        const b = err.body as {
          error?: string
          yourCount?: number
          theirCount?: number
        }
        if (b.error === 'insufficient') {
          return {
            ok: false,
            reason: 'insufficient',
            yourCount: b.yourCount ?? 0,
            theirCount: b.theirCount ?? 0,
          }
        }
        if (
          b.error === 'not_in_battle' ||
          b.error === 'venue_not_in_battle' ||
          b.error === 'not_found'
        ) {
          return { ok: false, reason: b.error }
        }
      }
      throw err
    }
  },
}
